import { jArticleListSchema, jArticleViewSchema } from './article';
import { z } from 'zod';

export enum SurveyFieldName {
    Binary = 'binary',
    Select = 'select',
    Radio = 'radio',
    Check = 'check',
    Rate = 'rate',
    Scale = 'scale',
    Text = 'text',
    Textarea = 'textarea',
    FileUpload = 'file-upload',
    GridRadio = 'grid-radio',
    GridCheck = 'grid-check',
}

export const surveyFieldKeysSchema = z.nativeEnum(SurveyFieldName);
export type SurveyFieldKeys = z.infer<typeof surveyFieldKeysSchema>;

export const surveyFieldBinaryDataSchema = z
    .object({
        title: z.string(),
    })
    .array();
export const surveyFieldSelectDataSchema = z
    .object({
        title: z.string(),
    })
    .array();
export const surveyFieldRadioDataSchema = z
    .object({
        title: z.string(),
    })
    .array();
export const surveyFieldCheckDataSchema = z
    .object({
        title: z.string(),
    })
    .array();
export const surveyFieldRateDataSchema = z.object({
    type: z.object({
        on: z.string(),
        off: z.string(),
    }),
    size: z.number().array(),
    index: z.number(),
});
export const surveyFieldScaleDataSchema = z.object({
    start: z.object({
        title: z.string(),
        value: z.number(),
    }),
    end: z.object({
        title: z.string(),
        value: z.number(),
    }),
    options: z
        .object({
            label: z.string(),
            value: z.number(),
        })
        .array(),
});
export const surveyFieldTextDataSchema = z.string();
export const surveyFieldTextareaDataSchema = z.string();
export const surveyFieldFileUploadDataSchema = z.string().array();
export const surveyFieldGridRadioDataSchema = z.object({
    lines: z.object({ title: z.string() }).array(),
    rows: z.object({ title: z.string() }).array(),
});
export const surveyFieldGridCheckDataSchema = z.object({
    lines: z.object({ title: z.string() }).array(),
    rows: z.object({ title: z.string() }).array(),
});

export const combinedSurveyFieldTypeSchema = z.union([
    surveyFieldBinaryDataSchema,
    surveyFieldSelectDataSchema,
    surveyFieldRadioDataSchema,
    surveyFieldCheckDataSchema,
    surveyFieldRateDataSchema,
    surveyFieldScaleDataSchema,
    surveyFieldTextDataSchema,
    surveyFieldTextareaDataSchema,
    surveyFieldFileUploadDataSchema,
    surveyFieldGridRadioDataSchema,
    surveyFieldGridCheckDataSchema,
]);

export type CombinedSurveyFieldType = z.infer<typeof combinedSurveyFieldTypeSchema>;

export const surveyFieldOptionBaseSchema = z.object({});
export const surveyFieldOptionCheckSchema = surveyFieldOptionBaseSchema.extend({
    lengthMin: z.boolean(),
    lengthMinValue: z.number(),
    lengthMax: z.boolean(),
    lengthMaxValue: z.number(),
});
export const surveyFieldOptionRateSchema = surveyFieldOptionBaseSchema.extend({
    types: z
        .object({
            on: z.string(),
            off: z.string(),
        })
        .array(),
});
export const surveyFieldOptionScaleSchema = surveyFieldOptionBaseSchema.extend({
    arr: z
        .object({
            label: z.string(),
            value: z.number(),
        })
        .array(),
});
export const surveyFieldOptionTextSchema = surveyFieldOptionBaseSchema.extend({
    length: z.boolean(),
    lengthMin: z.number(),
    lengthMax: z.number(),
});
export const surveyFieldOptionTextareaSchema = surveyFieldOptionBaseSchema.extend({
    length: z.boolean(),
    lengthMin: z.number(),
    lengthMax: z.number(),
});
export const surveyFieldOptionFileUploadSchema = surveyFieldOptionBaseSchema.extend({
    limit: z.number().or(z.boolean()),
    limitValue: z.number(),
    arr: z
        .object({
            name: z.string(),
            active: z.boolean(),
            mimetype: z.string(),
        })
        .array(),
});
export const surveyFieldOptionGridCheckSchema = surveyFieldOptionBaseSchema.extend({
    forceResponse: z.boolean(),
});
export const surveyFieldOptionGridRadioSchema = surveyFieldOptionBaseSchema.extend({
    forceResponse: z.boolean(),
});

export const combinedSurveyFieldOptionsSchema = z.union([
    surveyFieldOptionCheckSchema,
    surveyFieldOptionRateSchema,
    surveyFieldOptionScaleSchema,
    surveyFieldOptionTextSchema,
    surveyFieldOptionTextareaSchema,
    surveyFieldOptionFileUploadSchema,
    surveyFieldOptionGridCheckSchema,
    surveyFieldOptionGridRadioSchema,
]);

export const surveyFieldBaseSchema = z.object({
    mandatory: z.boolean(),
    condition: z.boolean(),
    uniqid: z.string(),
    label: z.string(),
    description: z.string(),
    name: surveyFieldKeysSchema,
    extraParams: z.object({}).optional(),
    widgetOptions: surveyFieldOptionBaseSchema.optional(),
});

export const surveyFieldBinarySchema = surveyFieldBaseSchema.extend({
    name: z.literal(SurveyFieldName.Binary),
    data: surveyFieldBinaryDataSchema,
});

export const surveyFieldSelectSchema = surveyFieldBaseSchema.extend({
    name: z.literal(SurveyFieldName.Select),
    data: surveyFieldSelectDataSchema,
});

export const surveyFieldRadioSchema = surveyFieldBaseSchema.extend({
    name: z.literal(SurveyFieldName.Radio),
    data: surveyFieldRadioDataSchema,
});

export const surveyFieldCheckSchema = surveyFieldBaseSchema.extend({
    name: z.literal(SurveyFieldName.Check),
    data: surveyFieldCheckDataSchema,
    widgetOptions: surveyFieldOptionCheckSchema,
});

export const surveyFieldRateSchema = surveyFieldBaseSchema.extend({
    name: z.literal(SurveyFieldName.Rate),
    data: surveyFieldRateDataSchema,
    widgetOptions: surveyFieldOptionRateSchema,
});

export const surveyFieldScaleSchema = surveyFieldBaseSchema.extend({
    name: z.literal(SurveyFieldName.Scale),
    data: surveyFieldScaleDataSchema,
    widgetOptions: surveyFieldOptionScaleSchema,
});

export const surveyFieldTextSchema = surveyFieldBaseSchema.extend({
    name: z.literal(SurveyFieldName.Text),
    data: surveyFieldTextDataSchema,
    widgetOptions: surveyFieldOptionTextSchema,
});

export const surveyFieldTextareaSchema = surveyFieldBaseSchema.extend({
    name: z.literal(SurveyFieldName.Textarea),
    data: surveyFieldTextareaDataSchema,
    widgetOptions: surveyFieldOptionTextareaSchema,
});

export const surveyFieldFileUploadSchema = surveyFieldBaseSchema.extend({
    name: z.literal(SurveyFieldName.FileUpload),
    data: surveyFieldFileUploadDataSchema,
    widgetOptions: surveyFieldOptionFileUploadSchema,
});

export const surveyFieldGridRadioSchema = surveyFieldBaseSchema.extend({
    name: z.literal(SurveyFieldName.GridRadio),
    data: surveyFieldGridRadioDataSchema,
    widgetOptions: surveyFieldOptionGridRadioSchema,
});

export const surveyFieldGridCheckSchema = surveyFieldBaseSchema.extend({
    name: z.literal(SurveyFieldName.GridCheck),
    data: surveyFieldGridCheckDataSchema,
    widgetOptions: surveyFieldOptionGridCheckSchema,
});

export const combinedSurveyFieldSchema = z.union([
    surveyFieldBinarySchema,
    surveyFieldSelectSchema,
    surveyFieldRadioSchema,
    surveyFieldCheckSchema,
    surveyFieldRateSchema,
    surveyFieldScaleSchema,
    surveyFieldTextSchema,
    surveyFieldTextareaSchema,
    surveyFieldFileUploadSchema,
    surveyFieldGridRadioSchema,
    surveyFieldGridCheckSchema,
]);

export type CombinedSurveyField = z.infer<typeof combinedSurveyFieldSchema>;
export type SurveyField = z.infer<typeof combinedSurveyFieldSchema>;
export const surveySectionSchema = z.object({
    uniqid: z.string(),
    title: z.string(),
    fields: combinedSurveyFieldSchema.array(),
});

export type SurveySection = z.infer<typeof surveySectionSchema>;

export const jPollSurveyListSchema = jArticleListSchema.extend({
    anonymousResponse: z.null(),
    multipleResponse: z.number(),
    publicResults: z.null(),
    pollProgression: z.object({
        colors: z.record(z.string()),
        maxRespondentsValue: z.number(),
        countRespondentsValue: z.number(),
    }),
    pollResponseShowProgression: z.number(),
    dateEnd: z.string(),
    status: z.object({
        name: z.string(),
        value: z.number(),
    }),
});
export type jPollSurveyList = z.infer<typeof jPollSurveyListSchema>;

export const jPollSurveyViewSchema = jArticleViewSchema.and(jPollSurveyListSchema).and(
    z.object({
        pollContent: surveySectionSchema.array().or(z.null()),
        pollLinks: z.null(),
        pollForceClose: z.null(),
        pollResponseUserVisibility: z.null(),
        pollResponseAuthorVisibility: z.null(),
        pollResponseShowRespondent: z.null(),
        pollBanner: z.tuple([]),
        pollMessage: z.string(),
    }),
);
export type jPollSurveyView = z.infer<typeof jPollSurveyViewSchema>;
